import { useState } from 'react';
import { CreditCard, Send, ShoppingCart, TrendingUp, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

const formatPrice = (price) => {
  if (!price) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const PaymentModal = ({ isOpen, onClose, ideaId, ideaTitle, paymentType, amount }) => {
  const [loading, setLoading] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
  });

  const typeConfig = {
    buy: {
      title: 'Purchase This Idea',
      icon: ShoppingCart,
      color: 'text-violet-400',
      buttonColor: 'bg-violet-500 hover:bg-violet-400',
      description: 'Complete your purchase to acquire full rights to this idea.',
    },
    invest: {
      title: 'Invest in Development',
      icon: TrendingUp,
      color: 'text-emerald-400',
      buttonColor: 'bg-emerald-500 hover:bg-emerald-400',
      description: 'Invest in the development of this idea and become a stakeholder.',
    },
  };

  const config = typeConfig[paymentType] || typeConfig.buy;
  const Icon = config.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/payments/create', {
        idea_id: ideaId,
        payment_type: paymentType,
        ...formData,
      });

      setPaymentResult(response.data);

      if (response.data.payment_url) {
        toast.success('Redirecting to payment...');
        window.open(response.data.payment_url, '_blank');
      } else {
        toast.success('Payment request submitted! Our team will contact you shortly.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create payment';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentResult(null);
    setFormData({ name: '', email: '', company: '' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border border-white/10 text-white max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-heading">
            <Icon className={`h-5 w-5 ${config.color}`} />
            <span>{config.title}</span>
          </DialogTitle>
        </DialogHeader>

        {!paymentResult ? (
          <>
            <div className="mb-4">
              <p className="text-slate-400 text-sm mb-3">{config.description}</p>
              <p className="text-slate-400 text-sm">
                Idea: <span className="text-white font-medium">{ideaTitle}</span>
              </p>
            </div>

            {/* Price Display */}
            <div className={`p-4 rounded-lg ${paymentType === 'buy' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'} mb-4`}>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Total Amount</span>
                <span className="text-2xl font-bold text-white">{formatPrice(amount)}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
                  placeholder="John Doe"
                  data-testid="payment-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
                  placeholder="john@company.com"
                  data-testid="payment-email-input"
                />
              </div>

              <div>
                <Label htmlFor="company" className="text-slate-300">Company (Optional)</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
                  placeholder="Acme Inc."
                  data-testid="payment-company-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  className="flex-1 border border-white/10 text-slate-300 hover:bg-white/5"
                  data-testid="payment-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 ${config.buttonColor} text-black font-semibold`}
                  data-testid="payment-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Proceed to Payment
                    </span>
                  )}
                </Button>
              </div>
            </form>

            <p className="text-xs text-slate-500 text-center mt-4">
              Secure payment processing by xMoney
            </p>
          </>
        ) : (
          /* Payment Result */
          <div className="py-4">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full ${paymentResult.payment_url ? 'bg-emerald-500/20' : 'bg-cyan-500/20'} flex items-center justify-center mx-auto mb-4`}>
                {paymentResult.payment_url ? (
                  <CreditCard className="h-8 w-8 text-emerald-400" />
                ) : (
                  <Send className="h-8 w-8 text-cyan-400" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {paymentResult.payment_url ? 'Payment Ready' : 'Request Submitted'}
              </h3>
              <p className="text-slate-400 text-sm">
                {paymentResult.message}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-slate-950/50 border border-white/5 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Amount</span>
                <span className="text-white font-medium">{formatPrice(paymentResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Status</span>
                <span className={`font-medium ${paymentResult.status === 'pending' ? 'text-amber-400' : 'text-cyan-400'}`}>
                  {paymentResult.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            {paymentResult.payment_url && (
              <Button
                onClick={() => window.open(paymentResult.payment_url, '_blank')}
                className={`w-full ${config.buttonColor} text-black font-semibold mb-3`}
                data-testid="payment-redirect-btn"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Complete Payment
              </Button>
            )}

            <Button
              onClick={handleClose}
              variant="ghost"
              className="w-full border border-white/10 text-slate-300 hover:bg-white/5"
              data-testid="payment-close-btn"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
